import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
const API_KEY = import.meta.env.VITE_API_KEY || '';

export interface JobResponse {
  job_id: string;
}

export interface PDFPageCSV {
  page_number: number;
  csv: string;
}

export interface JobStatusResponse {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  result?: {
    sheet_url?: string;
    pages?: PDFPageCSV[];
    error?: string;
  };
  error?: string;
}

export const uploadPDF = async (pageFiles: File[]): Promise<string> => {
  const formData = new FormData();

  // Append each page PDF file
  pageFiles.forEach((file) => {
    formData.append('pages', file);
  });

  try {
    const response = await axios.post<JobResponse>(`${API_BASE_URL}/api/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(API_KEY && { 'X-API-Key': API_KEY }),
      },
      timeout: 30000, // 30 seconds timeout for upload
    });

    if (response.status === 202) {
      return response.data.job_id;
    } else {
      throw new Error('Failed to upload PDF');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        throw new Error(
          error.response.data?.error ||
          `Server error: ${error.response.status}`
        );
      } else if (error.request) {
        throw new Error('No response from server. Please check your connection.');
      }
    }
    throw error;
  }
};

export const uploadFullPDF = async (pdfFile: File, selectedPages: number[]): Promise<string> => {
  const formData = new FormData();

  // Append the full PDF file
  formData.append('pdf', pdfFile);

  // Append selected page numbers
  selectedPages.forEach((pageNum) => {
    formData.append('selectedPages', pageNum.toString());
  });

  try {
    const response = await axios.post<JobResponse>(`${API_BASE_URL}/api/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(API_KEY && { 'X-API-Key': API_KEY }),
      },
      timeout: 30000, // 30 seconds timeout for upload
    });

    if (response.status === 202) {
      return response.data.job_id;
    } else {
      throw new Error('Failed to upload PDF');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        throw new Error(
          error.response.data?.error ||
          `Server error: ${error.response.status}`
        );
      } else if (error.request) {
        throw new Error('No response from server. Please check your connection.');
      }
    }
    throw error;
  }
};

export class ApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
  }
}

const getJobStatus = async (jobId: string): Promise<JobStatusResponse> => {
  try {
    const response = await axios.get<JobStatusResponse>(`${API_BASE_URL}/api/job/${jobId}`, {
      timeout: 15000, // 15 seconds timeout for status check
      headers: {
        ...(API_KEY && { 'X-API-Key': API_KEY }),
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        throw new ApiError(
          error.response.data?.error ||
          `Server error: ${error.response.status}`,
          error.response.status
        );
      } else if (error.request) {
        throw new ApiError('No response from server. Please check your connection.');
      }
    }
    throw error;
  }
};

const POLL_MAX_CONSECUTIVE_FAILURES = 4;
const POLL_MAX_TOTAL_MS = 8 * 60 * 1000;
const POLL_BACKOFF_CAP_MS = 15000;

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Polling cancelled'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error('Polling cancelled'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });

const friendlyPollError = (lastError: unknown): Error => {
  console.error('Polling failed:', lastError);
  if (lastError instanceof ApiError && lastError.status === 404) {
    return new Error(
      'The server restarted and your conversion was lost. Please try uploading again.'
    );
  }
  if (
    lastError instanceof Error &&
    lastError.message.includes('No response from server')
  ) {
    return lastError;
  }
  return new Error('Something went wrong on our side. Please try again.');
};

export const pollJobStatus = async (
  jobId: string,
  onStatusUpdate?: (status: JobStatusResponse) => void,
  pollInterval: number = 2000,
  signal?: AbortSignal
): Promise<JobStatusResponse> => {
  const startedAt = Date.now();
  let consecutiveFailures = 0;

  while (true) {
    if (signal?.aborted) {
      throw new Error('Polling cancelled');
    }
    if (Date.now() - startedAt > POLL_MAX_TOTAL_MS) {
      throw new Error(
        'Conversion is taking longer than expected. Your file may still be processing — please try again in a few minutes.'
      );
    }

    let status: JobStatusResponse;
    try {
      status = await getJobStatus(jobId);
    } catch (error) {
      if (signal?.aborted) {
        throw new Error('Polling cancelled');
      }
      consecutiveFailures++;
      if (consecutiveFailures >= POLL_MAX_CONSECUTIVE_FAILURES) {
        throw friendlyPollError(error);
      }
      const backoff = Math.min(
        pollInterval * 2 ** (consecutiveFailures - 1),
        POLL_BACKOFF_CAP_MS
      );
      await sleep(backoff, signal);
      continue;
    }

    consecutiveFailures = 0;
    if (signal?.aborted) {
      throw new Error('Polling cancelled');
    }
    if (onStatusUpdate) {
      onStatusUpdate(status);
    }
    if (status.status === 'completed') {
      return status;
    }
    if (status.status === 'failed') {
      throw new Error(status.error || 'Job failed');
    }
    await sleep(pollInterval, signal);
  }
};

